/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useSnackbar } from "notistack";
import { useAsyncData } from "./useAsyncData";
import * as drive from "../../../api/drive";
import { formatDate } from "./format";

const SHARE_LABELS = {
  public: "Public link",
  organisation: "My organisation",
  user: "Specific person",
};

/** Links and direct shares the user owns, plus what others have shared with them. */
// Module scope keeps the reference stable for useAsyncData.
const fetchShares = () => drive.listShares();

const SharedPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const state = useAsyncData(fetchShares, { owned: [], sharedWithMe: [] }, "Failed to load shares.");


  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/s/${token}`);
      enqueueSnackbar("Link copied.", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy the link.", { variant: "error" });
    }
  };

  const revoke = async (id) => {
    try {
      await drive.deleteShare(id);
      enqueueSnackbar("Share revoked.", { variant: "success" });
      state.reload();
    } catch (err) {
      enqueueSnackbar(err.message || "Could not revoke the share.", { variant: "error" });
    }
  };

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 20, fontWeight: 600, mb: 2 }}>Shared</Typography>

      {state.error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={state.reload}>Retry</Button>}>
          {state.error}
        </Alert>
      )}

      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Shared by me</Typography>
      <Paper variant="outlined" sx={{ mb: 4 }}>
        {state.owned.length === 0 ? (
          <Typography sx={{ p: 3, fontSize: 13.5, color: "secondaryText.color" }}>
            You haven't shared anything yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Access</TableCell>
                <TableCell>Permission</TableCell>
                <TableCell>Created</TableCell>
                <TableCell width={110} />
              </TableRow>
            </TableHead>
            <TableBody>
              {state.owned.map((share) => (
                <TableRow key={share.id} hover>
                  <TableCell sx={{ textTransform: "capitalize" }}>{share.resource_type}</TableCell>
                  <TableCell>
                    {/* `share_type` is computed by the API so clients don't each
                        re-derive it from which target column is populated. */}
                    <Chip size="small" label={SHARE_LABELS[share.share_type] ?? "Share"} sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{share.permission}</TableCell>
                  <TableCell>{formatDate(share.created_at)}</TableCell>
                  <TableCell align="right">
                    {share.share_token && (
                      <Tooltip title="Copy link">
                        <IconButton size="small" onClick={() => copyLink(share.share_token)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Revoke">
                      <IconButton size="small" onClick={() => revoke(share.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Shared with me</Typography>
      <Paper variant="outlined">
        {state.sharedWithMe.length === 0 ? (
          <Typography sx={{ p: 3, fontSize: 13.5, color: "secondaryText.color" }}>
            Nothing has been shared with you.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Via</TableCell>
                <TableCell>Permission</TableCell>
                <TableCell>Shared</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.sharedWithMe.map((share) => (
                <TableRow key={share.id} hover>
                  <TableCell sx={{ textTransform: "capitalize" }}>{share.resource_type}</TableCell>
                  <TableCell>
                    <Chip size="small" label={SHARE_LABELS[share.share_type] ?? "Share"} sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{share.permission}</TableCell>
                  <TableCell>{formatDate(share.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default SharedPage;
